/** Sonic-boom bow-shock wavefronts at the fist position during the active phase of a Punch attack. */
import Phaser from 'phaser';
import { Component } from './Component';
import { CRISP_SCALE } from '../ui/CrispUtil';
import type { AttackState } from './CombatComponent';
import { getFistPosition } from '../utils/PlayerGeometry';
import type { Position } from '../utils/Types';

/**
 * Each wavefront is a forward-bowing arc whose center is displaced BACKWARD from the fist.
 * The arc radius ≈ center offset so the arc's forward tip grazes the fist — exactly a Mach cone.
 * Inner wavefronts are tighter and brighter; outer ones spread wider and dimmer.
 */
const WAVES = [
  { centerOffset: 3,  radius: 4,  halfSweep: 0.90, alpha: 1.0  }, // compressed bright front
  { centerOffset: 7,  radius: 8,  halfSweep: 1.05, alpha: 0.75 }, // first trailing wave
  { centerOffset: 13, radius: 14, halfSweep: 1.20, alpha: 0.45 }, // outer dissipating wave
];

/**
 * Radial offsets from the arc radius — instead of wide lineWidths that bleed
 * forward at the apex, we stack thin arcs at nearby radii so the glow spreads
 * along the curvature, not through the centerline.
 */
const RADIAL_OFFSETS = [
  { dr: -3, alphaScale: 0.12 },
  { dr: -2, alphaScale: 0.25 },
  { dr: -1, alphaScale: 0.55 },
  { dr:  0, alphaScale: 1.0  },
  { dr:  1, alphaScale: 0.55 },
  { dr:  2, alphaScale: 0.25 },
  { dr:  3, alphaScale: 0.12 },
];
const CORE_LINE_WIDTH = 1.5;

const PEAK_ALPHA = 1.0;
const FADE_MS = 120;

const CANVAS_HALF = 32; // logical pixels — enough for outermost arc + glow bleed
const CANVAS_PX = CANVAS_HALF * 2 * CRISP_SCALE;
const INV_CRISP = 1 / CRISP_SCALE;
const TEX_KEY = '_punch-impact';

/** Flashes a Mach-cone bow shock at the fist — wavefronts bow forward and trail backward. */
export class PunchImpactComponent implements Component {
  private scene: Phaser.Scene;
  private sprite: Phaser.GameObjects.Sprite;
  private facing: Position;

  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private image: Phaser.GameObjects.Image;

  private currentAlpha = 0;
  private targetAlpha = 0;

  constructor(
    scene: Phaser.Scene,
    sprite: Phaser.GameObjects.Sprite,
    events: Phaser.Events.EventEmitter,
    facing: Position,
  ) {
    this.scene = scene;
    this.sprite = sprite;
    this.facing = facing;

    this.canvas = document.createElement('canvas');
    this.canvas.width = CANVAS_PX;
    this.canvas.height = CANVAS_PX;
    this.ctx = this.canvas.getContext('2d')!;

    if (!scene.textures.exists(TEX_KEY)) {
      scene.textures.addCanvas(TEX_KEY, this.canvas);
      scene.textures.get(TEX_KEY).setFilter(Phaser.Textures.FilterMode.LINEAR);
    }

    this.image = scene.add.image(0, 0, TEX_KEY)
      .setScale(INV_CRISP)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setVisible(false);

    events.on('attack-state-changed', this.onAttackStateChanged, this);
  }

  private onAttackStateChanged(state: AttackState, attackType: string): void {
    if (attackType !== 'punch') {
      this.targetAlpha = 0;
      return;
    }
    this.targetAlpha = state === 'active' ? PEAK_ALPHA : 0;
  }

  update(delta: number): void {
    if (this.targetAlpha > 0) {
      this.currentAlpha = this.targetAlpha;
    } else if (this.currentAlpha > 0) {
      this.currentAlpha = Math.max(0, this.currentAlpha - PEAK_ALPHA * (delta / FADE_MS));
    }

    if (this.currentAlpha <= 0.01) {
      this.image.setVisible(false);
      this.currentAlpha = 0;
      return;
    }

    const { x: cx, y: cy, nx, ny } = getFistPosition(this.sprite.x, this.sprite.y, this.facing);

    this.drawBowShock(nx, ny);
    (this.scene.textures.get(TEX_KEY) as Phaser.Textures.CanvasTexture).refresh();

    this.image.setPosition(cx, cy);
    this.image.setAlpha(this.currentAlpha);
    this.image.setDepth(this.sprite.depth + 1);
    this.image.setVisible(true);
  }

  /**
   * Each arc's center is displaced backward from the canvas center (fist position).
   * The arc sweeps around the forward direction, bowing out in front of the fist.
   */
  private drawBowShock(nx: number, ny: number): void {
    const S = CRISP_SCALE;
    const center = CANVAS_HALF * S;
    const forwardAngle = Math.atan2(ny, nx);
    const backAngle = forwardAngle + Math.PI;

    this.ctx.clearRect(0, 0, CANVAS_PX, CANVAS_PX);
    this.ctx.lineCap = 'round';

    for (const wave of WAVES) {
      // Displace arc center backward from fist
      const arcCx = center + Math.cos(backAngle) * wave.centerOffset * S;
      const arcCy = center + Math.sin(backAngle) * wave.centerOffset * S;

      for (const offset of RADIAL_OFFSETS) {
        this.drawArcGradient(
          arcCx, arcCy,
          (wave.radius + offset.dr) * S,
          forwardAngle - wave.halfSweep,
          forwardAngle + wave.halfSweep,
          CORE_LINE_WIDTH * S,
          wave.alpha * offset.alphaScale,
        );
      }
    }
  }

  /** Draws an arc in segments with a sin-bell alpha falloff — bright at center, fading at both tips. */
  private drawArcGradient(
    cx: number, cy: number, radius: number,
    startAngle: number, endAngle: number,
    lineWidth: number, peakAlpha: number,
  ): void {
    const SEGMENTS = 20;
    const span = endAngle - startAngle;
    this.ctx.lineWidth = lineWidth;
    this.ctx.lineCap = 'butt';

    for (let i = 0; i < SEGMENTS; i++) {
      const tMid = (i + 0.5) / SEGMENTS;
      const brightness = Math.sin(tMid * Math.PI); // 0 at tips, 1 at center
      this.ctx.strokeStyle = `rgba(255, 255, 255, ${peakAlpha * brightness})`;
      this.ctx.beginPath();
      this.ctx.arc(cx, cy, radius, startAngle + (i / SEGMENTS) * span, startAngle + ((i + 1) / SEGMENTS) * span, false);
      this.ctx.stroke();
    }

    this.ctx.lineCap = 'round';
  }

  destroy(): void {
    this.image.destroy();
    if (this.scene.textures.exists(TEX_KEY)) {
      this.scene.textures.remove(TEX_KEY);
    }
  }
}
